import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	HostListener,
	inject,
	Input,
	signal,
} from '@angular/core';
import { BehaviorSubject, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { MoveToRootModule } from '~app-client/core/directives';
import { MenuFloatingPosition } from '~ng-common-lib/ui/menu/components/floating/floating.component';

import { BaseMenu } from '../../types/menu';

export enum WidthSize {
	DEFAULT = 'DEFAULT',
	FILTER = 'FILTER',
	SMALL = 'SMALL',
	PX680 = 'PX680',
	PX320 = 'PX320',
	PX280 = 'PX280',
	PX240 = 'PX240',
	PX200 = 'PX200',
	PX80 = 'PX80',
	AUTO = 'AUTO',
	ANCHOR = 'ANCHOR',
}

export const widthSizeMap: Record<WidthSize, number | string> = {
	[WidthSize.DEFAULT]: 360,
	[WidthSize.FILTER]: 320,
	[WidthSize.SMALL]: 216,
	[WidthSize.PX680]: 680,
	[WidthSize.PX320]: 320,
	[WidthSize.PX280]: 280,
	[WidthSize.PX240]: 240,
	[WidthSize.PX200]: 200,
	[WidthSize.PX80]: 80,
	[WidthSize.AUTO]: 'auto-fit',
	[WidthSize.ANCHOR]: 'anchor',
};

@Component({
	selector: 'floating-menu',
	templateUrl: './floating.component.html',
	styleUrls: ['./floating.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [CommonModule, MoveToRootModule],
})
export class FloatingMenuComponent extends BaseMenu {
	private static readonly LEAVE_ANIMATION_MS = 100;
	private readonly cdRef = inject(ChangeDetectorRef);

	@Input('anchor')
	public set anchor(anchor: HTMLElement | undefined) {
		this.anchor$$.next(anchor);
	}

	public get anchor(): HTMLElement | undefined {
		return this.anchor$$.value;
	}

	@Input()
	public widthSize: WidthSize = WidthSize.DEFAULT;

	@Input()
	public lightBg = false;

	@Input()
	public bordered = false;

	@Input()
	public maxHeight: string | null = null;

	@Input()
	public stickyFooter = false;

	@Input()
	public hasPaddingTop = true;

	@Input()
	public fullHeight = false;

	@Input()
	public hasBackdrop = true;

	@Input('forcePosition')
	public get forcePosition(): MenuFloatingPosition | null {
		return this.$forcePosition();
	}

	public set forcePosition(value: MenuFloatingPosition | null) {
		this.$forcePosition.set(value);
	}

	public readonly $forcePosition = signal<MenuFloatingPosition | null>(null);

	public readonly $positionClasses = computed(() => {
		const position = this.$forcePosition();
		return {
			TOP: position === 'TOP',
			// 'BOTTOM': position === 'BOTTOM',
			// 'LEFT': position === 'LEFT',
			RIGHT: position === 'RIGHT',
		};
	});

	public readonly widthSizeMap = widthSizeMap;

	private _animationFrameId: number | null = null;
	private _closeTimeoutId: number | null = null;
	public isClosing = false;

	private readonly anchor$$ = new BehaviorSubject<HTMLElement | undefined>(undefined);
	private readonly anchor$ = this.anchor$$.asObservable();

	private readonly visible$$ = new BehaviorSubject(false);
	private readonly visible$ = this.visible$$.asObservable();

	private readonly resized$$ = new BehaviorSubject(null);
	private readonly resized$ = this.resized$$.asObservable();

	public readonly box$ = this.visible$.pipe(
		switchMap((visible) => {
			if (!visible) {
				return of(null);
			}

			return this.anchor$.pipe(
				switchMap((anchor) => {
					return this.resized$.pipe(
						map(() => {
							if (!anchor) {
								return null;
							}

							const {
								top: anchorTop,
								left: anchorLeft,
								width: anchorWidth,
								height: anchorHeight,
							} = anchor.getBoundingClientRect();

							const anchorRight = anchorLeft + anchorWidth;
							const anchorBottom = anchorTop + anchorHeight;

							const { innerWidth: windowWidth, innerHeight: windowHeight } = window;

							const availableLeft = anchorRight;
							const availableRight = windowWidth - anchorRight;
							const availableTop = anchorTop;
							const availableBottom = windowHeight - anchorBottom;

							const forcePosition = this.$forcePosition();
							const mappedWidth = this.widthSizeMap[this.widthSize];
							const resolvedWidth =
								this.widthSize === WidthSize.ANCHOR ? anchorWidth : mappedWidth;
							const menuWidth = typeof resolvedWidth === 'number' ? resolvedWidth : 0;
							const sideGap = 12;
							const spaceRight = windowWidth - anchorRight;
							const spaceLeft = anchorLeft;

							let addAtTop: boolean;
							let addAtLeft: boolean;
							let maxWidth: number;
							let maxHeight: number;

							if (forcePosition === 'RIGHT') {
								const fitsRight = spaceRight >= menuWidth + sideGap;
								const fitsLeft = spaceLeft >= menuWidth + sideGap;
								addAtLeft = !fitsRight && (fitsLeft || spaceLeft > spaceRight);
								addAtTop = availableTop > availableBottom;
								maxWidth = (addAtLeft ? spaceLeft : spaceRight) - sideGap;
								maxHeight = addAtTop ? availableTop : windowHeight - anchorTop;
							} else if (forcePosition === 'TOP') {
								addAtTop = true;
								addAtLeft = true;
								maxWidth = addAtLeft ? anchorRight : windowWidth - anchorLeft;
								maxHeight = anchorTop - 16;
							} else {
								addAtTop = availableTop > availableBottom;
								addAtLeft = availableLeft > availableRight;
								maxWidth = addAtLeft ? anchorRight : windowWidth - anchorLeft;
								maxHeight = addAtTop
									? anchorTop - 16
									: windowHeight - anchorBottom - 16;
							}

							const resolvedMinWidth =
								typeof resolvedWidth === 'number'
									? Math.min(resolvedWidth, Math.max(maxWidth, 0))
									: resolvedWidth;

							return {
								top: anchorTop,
								bottom: windowHeight - anchorBottom,
								anchorHeight: anchorHeight,
								left: anchorLeft,
								right: windowWidth - anchorRight,
								anchorWidth: anchorWidth,
								positionAtTopOfTarget: addAtTop,
								positionAtLeftOfTarget: addAtLeft,
								maxWidth,
								maxHeight,
								baseWidth: resolvedWidth,
								minWidth: resolvedMinWidth,
							};
						})
					);
				})
			);
		})
	);

	public open(): void {
		if (this._closeTimeoutId !== null) {
			clearTimeout(this._closeTimeoutId);
			this._closeTimeoutId = null;
		}

		this.isClosing = false;
		this.visible$$.next(true);
		this.opened$$.next();
	}

	public close(): void {
		this.isClosing = true;
		this.cdRef.detectChanges();

		if (this._closeTimeoutId !== null) {
			clearTimeout(this._closeTimeoutId);
		}

		this._closeTimeoutId = window.setTimeout(() => {
			this.visible$$.next(false);
			this.isClosing = false;
			this.closed$$.next();
			this._closeTimeoutId = null;
		}, FloatingMenuComponent.LEAVE_ANIMATION_MS);
	}

	public toggle(): void {
		if (this.visible$$.value) {
			this.close();
		} else {
			this.open();
		}
	}

	@HostListener('window:resize')
	@HostListener('window:orientationchange')
	public _onResize(): void {
		if (this._animationFrameId) {
			cancelAnimationFrame(this._animationFrameId);
		}

		this._animationFrameId = requestAnimationFrame(() => {
			this.resized$$.next(null);
		});
	}
}
